import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { Loader2, Printer } from "lucide-react";
import { api, formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import InvoiceDocument, { FitWidth, InvoicePage2, InvoiceEquipmentPage } from "@/components/invoices/InvoiceDocument";
import { hasPage2, sortInvoices, isEquipmentPageJob } from "@/lib/invoice";

const COPIES_NO_EMAIL2 = 3; // print 3 copies unless the customer has an Email2
const COPIES_WITH_EMAIL2 = 2; // customers with an Email2 get 2 copies
const COPY_LABELS = ["", "Maintenance", "Accounts Payable"]; // top-right stamp per copy index

const getEmail2 = (data) => {
  const key = Object.keys(data || {}).find((k) => /email\s*2/i.test(k));
  return key ? String(data[key] ?? "").trim() : "";
};

// How many copies to print per invoice: 1 when the source customer has an Email2, otherwise 3.
async function computeCopies(invoices, bulk) {
  const copies = {};
  invoices.forEach((inv) => { copies[inv.id] = 1; });
  if (!bulk) return copies;
  const stops = invoices
    .filter((i) => i.source?.record_id && i.source?.db_id)
    .map((i) => ({ db_id: i.source.db_id, table: i.source.table, record_id: i.source.record_id }));
  let records = {};
  if (stops.length) {
    try { records = (await api.post("/route-setup/records", { stops })).data.records || {}; } catch { records = {}; }
  }
  invoices.forEach((inv) => {
    const rec = inv.source?.record_id ? records[inv.source.record_id] : null;
    copies[inv.id] = rec && getEmail2(rec.data) ? COPIES_WITH_EMAIL2 : COPIES_NO_EMAIL2;
  });
  return copies;
}

export default function InvoicePrint() {
  const { invoiceId, batchId } = useParams();
  const [search] = useSearchParams();
  const listParams = invoiceId || batchId ? null : Object.fromEntries(search.entries());
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [withPage2, setWithPage2] = useState(false);

  useEffect(() => {
    const picked = listParams?.selected ? JSON.parse(localStorage.getItem("print-selected-ids") || "[]") : null;
    const load = picked ? (picked.length ? api.post("/invoices/by-ids", { ids: picked }).then((r) => r.data) : Promise.resolve([]))
      : listParams ? api.get("/invoices", { params: listParams }).then((r) => r.data)
      : batchId ? api.get("/invoices", { params: { batch_id: batchId } }).then((r) => r.data)
      : api.get(`/invoices/${invoiceId}`).then((r) => [r.data]);
    Promise.all([load, api.get("/invoice-settings")])
      .then(async ([invoices, s]) => {
        if (!invoices.length) throw new Error("No invoices to print");
        const sorted = picked || listParams ? sortInvoices(invoices) : invoices;
        const copies = await computeCopies(sorted, !invoiceId);
        setData({ invoices: sorted, settings: s.data, copies });
      })
      .catch((e) => setError(formatError(e)));
  }, [invoiceId, batchId, search]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!data) return;
    document.title = !invoiceId ? `Invoices batch (${data.invoices.length})` : `Invoice ${data.invoices[0].number}`;
    const fit = () => {
      const h = Math.max(...[...document.querySelectorAll("[data-testid='invoice-document']")].map((el) => el.offsetHeight), 1056);
      document.documentElement.style.setProperty("--print-zoom", String(Math.min(1, 1050 / h)));
    };
    window.addEventListener("beforeprint", fit);
    const t = setTimeout(() => { fit(); window.print(); }, 800);
    return () => { clearTimeout(t); window.removeEventListener("beforeprint", fit); };
  }, [data, invoiceId]);

  if (error) return <p className="p-8 text-red-400" data-testid="print-error">{error}</p>;
  if (!data) return <div className="p-8"><Loader2 className="animate-spin text-primary" /></div>;
  return (
    <div className="min-h-screen bg-slate-200 print:bg-white py-8 print:py-0" data-testid="invoice-print-page">
      <div className="max-w-[848px] mx-auto px-4 print:px-0 print:max-w-none">
        <div className="no-print flex items-center justify-between mb-4">
          <p className="text-slate-700 text-sm" data-testid="print-count">{data.invoices.length} invoice{data.invoices.length === 1 ? "" : "s"}</p>
          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer select-none" data-testid="include-page2-toggle">
              <input type="checkbox" checked={withPage2} onChange={(e) => setWithPage2(e.target.checked)} className="h-4 w-4 accent-primary" data-testid="include-page2-checkbox" />
              Include page 2
            </label>
            <Button onClick={() => window.print()} data-testid="print-button"><Printer size={16} /> Print or save as PDF</Button>
          </div>
        </div>
        <div className="space-y-8 print:space-y-0">
          {data.invoices.flatMap((inv) => {
            const n = data.copies?.[inv.id] ?? 1;
            const out = Array.from({ length: n }).flatMap((_, ci) => {
              const label = COPY_LABELS[ci] || "";
              const pages = [
                <div key={`${inv.id}-c${ci}-p1`} className="print-page" data-testid={`invoice-copy-${inv.id}-${ci}`}>
                  <FitWidth><InvoiceDocument invoice={inv} settings={data.settings} copyLabel={label} /></FitWidth>
                </div>,
              ];
              if (isEquipmentPageJob(inv.customer?.name)) pages.push(
                <div key={`${inv.id}-c${ci}-eq`} className="print-page" data-testid={`invoice-equip-${inv.id}-${ci}`}>
                  <FitWidth><InvoiceEquipmentPage invoice={inv} settings={data.settings} /></FitWidth>
                </div>
              );
              return pages;
            });
            // Page 2 (special notes / tax) prints only once per invoice, no matter how many copies.
            if (withPage2 && hasPage2(inv)) out.push(
              <div key={`${inv.id}-p2`} className="print-page" data-testid={`invoice-page2-${inv.id}-0`}>
                <FitWidth><InvoicePage2 invoice={inv} settings={data.settings} /></FitWidth>
              </div>
            );
            return out;
          })}
        </div>
      </div>
    </div>
  );
}
